import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  mockAdminCustomers,
  mockCompanies,
  mockAddresses,
  mockQuotations,
  mockOrders,
  mockAiPreviews,
} from '@/mocks/adminCustomers';

type TabKey = 'profile' | 'company' | 'addresses' | 'quotations' | 'orders' | 'aiPreviews';

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: 'profile', label: 'admin.customerDetail.profile', icon: 'ri-user-line' },
  { key: 'company', label: 'admin.customerDetail.company', icon: 'ri-building-line' },
  { key: 'addresses', label: 'admin.customerDetail.addresses', icon: 'ri-map-pin-line' },
  { key: 'quotations', label: 'admin.customerDetail.quotations', icon: 'ri-file-list-3-line' },
  { key: 'orders', label: 'admin.customerDetail.orders', icon: 'ri-shopping-bag-line' },
  { key: 'aiPreviews', label: 'admin.customerDetail.aiPreviews', icon: 'ri-image-ai-line' },
];

export default function AdminCustomerDetailPage() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabKey>('profile');

  // Find customer from mock data
  const customer = mockAdminCustomers.find((c) => c.id === id);
  const company = mockCompanies.find((c) => c.owner_user_id === id);
  const addresses = mockAddresses.filter((a) => a.user_id === id);
  const quotations = mockQuotations.filter((q) => q.user_id === id);
  const orders = mockOrders.filter((o) => o.user_id === id);
  const aiPreviews = mockAiPreviews.filter((p) => p.user_id === id);

  if (!customer) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-stone-500">Customer not found</p>
          <button
            onClick={() => navigate('/admin/customers')}
            className="mt-4 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors whitespace-nowrap"
          >
            {t('admin.customerDetail.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 md:px-6 lg:px-8 pt-6">
        <button
          onClick={() => navigate('/admin/customers')}
          className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          {t('admin.customerDetail.back')}
        </button>

        <div className="flex items-center gap-4 mt-4 mb-6">
          {customer.avatar_url ? (
            <img
              src={customer.avatar_url}
              alt={customer.full_name}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-lg font-bold text-amber-700">
              {customer.full_name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-stone-900 truncate">{customer.full_name}</h1>
            <p className="text-sm text-stone-500 truncate">{customer.email}</p>
          </div>
          <div className="ml-auto shrink-0">
            <span
              className={`inline-flex px-3 py-1 text-xs font-medium rounded-full border whitespace-nowrap ${
                customer.role === 'super_admin'
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : customer.role === 'admin'
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-stone-100 text-stone-700 border-stone-200'
              }`}
            >
              {customer.role}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'bg-amber-600 text-white'
                  : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'
              }`}
            >
              <i className={tab.icon} />
              {t(tab.label)}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 md:px-6 lg:px-8 py-6 pb-10">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-stone-900 mb-4">{t('admin.customerDetail.basicInfo')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoField label="ID" value={customer.id} />
              <InfoField label="Full Name" value={customer.full_name} />
              <InfoField label="Email" value={customer.email} />
              <InfoField label="Phone" value={customer.phone || '-'} />
              <InfoField label="Role" value={customer.role} />
              <InfoField label="Provider" value={customer.provider} />
              <InfoField label="Locale" value={customer.locale || '-'} />
              <InfoField label="Default Currency" value={customer.default_currency || '-'} />
              <InfoField label="Joined" value={new Date(customer.created_at).toLocaleString()} />
            </div>

            <div className="mt-8 pt-6 border-t border-stone-200">
              <h2 className="text-lg font-semibold text-stone-900 mb-4">{t('admin.customerDetail.contactInfo')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField label="Company" value={customer.company_name || '-'} />
                <InfoField label="Country" value={customer.country || '-'} />
              </div>
            </div>
          </div>
        )}

        {/* Company Tab */}
        {activeTab === 'company' && (
          <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
            {company ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField label="Company Name" value={company.company_name} />
                <InfoField label="Business Type" value={company.business_type || '-'} />
                <InfoField label="Country" value={company.country || '-'} />
                <InfoField label="Website" value={company.website || '-'} />
                <InfoField label="Contact Email" value={company.contact_email || '-'} />
                <InfoField label="Contact Phone" value={company.contact_phone || '-'} />
                <InfoField label="Tax ID" value={company.tax_id || '-'} />
                <InfoField label="Company Size" value={company.company_size || '-'} />
                <InfoField label="Status" value={company.status} />
                <InfoField label="Created" value={new Date(company.created_at).toLocaleString()} />
              </div>
            ) : (
              <p className="text-sm text-stone-400 text-center py-8">{t('admin.customerDetail.noCompany')}</p>
            )}
          </div>
        )}

        {/* Addresses Tab */}
        {activeTab === 'addresses' && (
          <div className="space-y-4">
            {addresses.length > 0 ? (
              addresses.map((addr) => (
                <div
                  key={addr.id}
                  className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-stone-100 text-stone-700 border border-stone-200 uppercase">
                        {addr.address_type}
                      </span>
                      {addr.is_default && (
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                          Default
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <InfoField label="Recipient" value={addr.recipient_name} />
                    <InfoField label="Phone" value={addr.phone} />
                    <InfoField label="Address" value={`${addr.address_line1}${addr.address_line2 ? `, ${addr.address_line2}` : ''}`} />
                    <InfoField label="City" value={addr.city} />
                    <InfoField label="Country" value={addr.country} />
                    <InfoField label="Postal Code" value={addr.postal_code} />
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-xl border border-stone-200 p-8 shadow-sm text-center">
                <p className="text-sm text-stone-400">{t('admin.customerDetail.noAddresses')}</p>
              </div>
            )}
          </div>
        )}

        {/* Quotations Tab */}
        {activeTab === 'quotations' && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            {quotations.length > 0 ? (
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-stone-50 border-b border-stone-200">
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Product</th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Quantity</th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {quotations.map((q) => (
                    <tr key={q.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-stone-900">{q.product_name}</td>
                      <td className="px-4 py-3 text-sm text-stone-600">{q.quantity.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={q.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-500">
                        {new Date(q.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-stone-400 text-center py-8">{t('admin.customerDetail.noQuotations')}</p>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            {orders.length > 0 ? (
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-stone-50 border-b border-stone-200">
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Product</th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Payment</th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Total</th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {orders.map((o) => (
                    <tr key={o.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-stone-900">{o.product_name}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={o.status} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={o.payment_status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-600">
                        ${o.total_amount?.toLocaleString() || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-500">
                        {new Date(o.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-stone-400 text-center py-8">{t('admin.customerDetail.noOrders')}</p>
            )}
          </div>
        )}

        {/* AI Previews Tab */}
        {activeTab === 'aiPreviews' && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            {aiPreviews.length > 0 ? (
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-stone-50 border-b border-stone-200">
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Product</th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Finish Type</th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {aiPreviews.map((p) => (
                    <tr key={p.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-stone-900">{p.product_name}</td>
                      <td className="px-4 py-3 text-sm text-stone-600">{p.finish_type}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-500">
                        {new Date(p.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-stone-400 text-center py-8">{t('admin.customerDetail.noPreviews')}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-1">{label}</dt>
      <dd className="text-sm text-stone-900 break-all">{value}</dd>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    quoted: 'bg-blue-50 text-blue-700 border-blue-200',
    accepted: 'bg-green-50 text-green-700 border-green-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    production: 'bg-amber-50 text-amber-700 border-amber-200',
    paid: 'bg-green-50 text-green-700 border-green-200',
    completed: 'bg-green-50 text-green-700 border-green-200',
  };

  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${styles[status] || 'bg-stone-100 text-stone-700 border-stone-200'}`}>
      {status}
    </span>
  );
}